import _ from "lodash";
/**--external-- */
import React from "react";
import update from "immutability-helper";
import classnames from "classnames";

/**-internal-- */
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";

/**--relative-- */
import GenericCard from "./components/GenericCard";
import GenericCardView from "./components/GenericCardView";
import classes from "./GenericCardList.scss";
import { generateRandomIdV2 } from "Utils";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";

class GenericCardList extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      focusedItem: "",
    };
  }

  componentDidMount() {
    const { value, resetOptions, isUpdateActionsAllowed } = this.props;

    if (_.isEmpty(value) && !_.isEmpty(resetOptions)) {
      const updatedValue = _.map(resetOptions, option => ({
        id: option.id,
        label: option.label,
        isSelected: false,
        // specifically writing following line to make sync between old and new UI
        value: option.label,
      }));

      this.updateValue(updatedValue);
    }
    if (isUpdateActionsAllowed && !_.isEmpty(value)) {
      // Filter out items where value is empty

      const filteredValue = this.filterEmptyValueOrLabel(value);
      // Only update if there are changes
      if (!_.isEqual(filteredValue, value)) {
        this.updateValue(filteredValue);
      }
    }
  }

  componentWillUnmount() {
    const { value, isUpdateActionsAllowed } = this.props;
    if (isUpdateActionsAllowed && !_.isEmpty(value)) {
      // Filter out items where value is empty
      const filteredValue = this.filterEmptyValueOrLabel(value);
      // Only update if there are changes
      if (!_.isEqual(filteredValue, value)) {
        this.updateValue(filteredValue);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { options, isUpdateActionsAllowed } = this.props;
    const { focusedItem } = this.state;

    // Check if options have changed
    if (!_.isEqual(prevProps.options, options) && isUpdateActionsAllowed) {
      const updatedValue = _.map(options, option => {
        const shouldDeselect =
          focusedItem === option.id && _.isEmpty(_.get(option, "label", ""));
        return {
          id: option.id,
          label: option.label,
          isSelected: shouldDeselect ? false : option.isSelected,
          value: option.value,
        };
      });

      if (!_.isEmpty(updatedValue)) {
        this.updateValue(updatedValue);
      }
    }
  }

  filterEmptyValueOrLabel = value => {
    return _.filter(
      value,
      item =>
        !_.isEmpty(_.get(item, "value", "")) ||
        !_.isEmpty(_.get(item, "label", ""))
    );
  };

  onCardUpdate = ({ cardId, actionType, updatedLabel = "" }) => {
    const {
      isUpdateActionsAllowed,
      value,
      allowMultiSelect,
      options,
      updateSingleSelectValueAsList,
    } = this.props;

    const currentValue = value ?? [];

    let updatedValue = currentValue;

    // Handle actions that are allowed only if isUpdateActionsAllowed is true
    if (isUpdateActionsAllowed) {
      switch (actionType) {
        case "SELECT": {
          const indexToUpdate = _.findIndex(currentValue, { id: cardId });
          if (indexToUpdate !== -1) {
            updatedValue = update(currentValue, {
              [indexToUpdate]: { isSelected: { $set: true } },
            });
          }
          break;
        }
        case "DESELECT": {
          const indexToUpdate = _.findIndex(currentValue, { id: cardId });
          if (indexToUpdate !== -1) {
            updatedValue = update(currentValue, {
              [indexToUpdate]: { isSelected: { $set: false } },
            });
          }
          break;
        }
        case "UPDATE_TITLE": {
          const indexToUpdate = _.findIndex(currentValue, { id: cardId });
          if (indexToUpdate !== -1) {
            updatedValue = update(currentValue, {
              [indexToUpdate]: {
                label: { $set: updatedLabel },
                // specifically writing following line to make sync between old and new UI
                value: { $set: updatedLabel },
              },
            });
          }
          break;
        }
        case "DELETE": {
          updatedValue = _.filter(currentValue, item => item.id !== cardId);
          if (_.isEmpty(updatedValue)) {
            const { resetOptions = [] } = this.props;

            updatedValue = _.map(resetOptions, option => {
              return {
                id: option.id,
                label: option.label,
                isSelected: false,
                // specifically writing following line to make sync between old and new UI
                value: option.label,
              };
            });
          }
          break;
        }
        case "ADD": {
          const newItemId = generateRandomIdV2();
          updatedValue = [
            ...currentValue,
            // specifically updating value to make sync between old and new UI
            { id: newItemId, label: "", isSelected: false, value: "" },
          ];
          this.setState({ focusedItem: newItemId });
          break;
        }
        default:
          break;
      }
    }

    // Handle multi-select or single-select logic
    else if (allowMultiSelect) {
      switch (actionType) {
        case "SELECT":
          // There could be cases where the value is not an array but an empty string
          if (!_.isArray(updatedValue)) {
            updatedValue = [];
          }
          updatedValue = update(updatedValue, { $push: [cardId] });
          break;
        case "DESELECT": {
          const index = _.indexOf(currentValue, cardId);
          if (index !== -1) {
            updatedValue = update(updatedValue, { $splice: [[index, 1]] });
          }
          break;
        }
        default:
          break;
      }
    } else {
      switch (actionType) {
        case "SELECT":
          updatedValue = updateSingleSelectValueAsList ? [cardId] : cardId;
          break;
        case "DESELECT":
          updatedValue = updateSingleSelectValueAsList ? [] : "";
          break;
        default:
          break;
      }
    }

    // Update the value if it has changed
    if (!_.isEqual(updatedValue, currentValue)) {
      this.updateValue(updatedValue, { nodes: options });
    }
  };

  onClickEdit = ({ cardId }) => {
    this.setState({ focusedItem: cardId });
  };

  onBlurCardInputField = () => {
    this.setState({ focusedItem: "" });
  };

  renderEdit = () => {
    const {
      options,
      value,
      allowMultiSelect,
      isUpdateActionsAllowed,
      disabled,
      t,
      showSubtextUnderInfo,
      totalColumns,
    } = this.props;

    const { focusedItem } = this.state;
    const isDisabled = _.isEmpty(_.get(_.last(value), "label", ""));

    const addButtonClasses = classnames({
      "text-label": true,
      [classes.addAnotherButton]: true,
      [classes.addButtonDisabled]: isDisabled,
    });

    const addButtonContainerClasses = classnames({
      [classes.addButtonContainer]: true,
      [classes.addButtonDisabledContainer]: isDisabled,
    });

    return (
      <div className={classes.container}>
        <div
          className={classes.listContainer}
          style={{ gridTemplateColumns: `repeat(${totalColumns}, 1fr)` }}
        >
          {_.map(isUpdateActionsAllowed ? value : options, item => {
            const itemId = item.id;
            return (
              <GenericCard
                key={itemId}
                item={item}
                isUpdateActionsAllowed={isUpdateActionsAllowed}
                disabled={disabled}
                isFocused={_.isEqual(focusedItem, itemId)}
                isSelected={
                  _.includes(value, itemId) || _.get(item, "isSelected", false)
                }
                onCardUpdate={this.onCardUpdate}
                allowMultiSelect={allowMultiSelect}
                onBlurCardInputField={this.onBlurCardInputField}
                onClickEdit={this.onClickEdit}
                showSubtextUnderInfo={showSubtextUnderInfo}
              />
            );
          })}
        </div>
        {isUpdateActionsAllowed ? (
          <div
            className={addButtonContainerClasses}
            onClick={() => {
              if (!isDisabled) {
                this.onCardUpdate({ actionType: "ADD" });
              }
            }}
          >
            <AddCircleOutlined
              variant={isDisabled ? "subtle" : "link"}
              size="xx-small"
            />

            <div className={addButtonClasses}>{t("common:add_another")}</div>
          </div>
        ) : null}
      </div>
    );
  };

  shouldShowViewEmpty = () => {
    const { value, isUpdateActionsAllowed } = this.props;
    let updatedValue = value;
    if (isUpdateActionsAllowed) {
      // As the options will be dynamic, we need to filter out the selected ones
      updatedValue = _.filter(value, item => _.get(item, "isSelected", false));
    }
    return _.isEmpty(updatedValue);
  };

  renderView = () => {
    const { options, value } = this.props;
    const itemList = _.filter(
      options,
      item => _.includes(value, item.id) || _.get(item, "isSelected", false)
    );

    return (
      <div className={classes.viewContainer}>
        {_.map(itemList, item => {
          return <GenericCardView key={item.id} item={item} />;
        })}
      </div>
    );
  };
}

export default I18nHOC({ resource: ["common"] })(GenericCardList);

GenericCardList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  value: [],
  allowMultiSelect: false,
  isUpdateActionsAllowed: false,
  showSubtextUnderInfo: false,
  totalColumns: 2,
  updateSingleSelectValueAsList: false,
};

GenericCardList.displayName = "GenericCardList";
